import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { useClubMembers } from '@/hooks/clubs'
import { useTravelingBooks } from '@/hooks/traveling'
import { CreateTripModal } from './CreateTripModal'
import { TripCard } from './TripCard'

/** Traveling books of a club, on the club page */
export function TravelingBooksSection({ clubId }: { clubId: string }) {
  const { t } = useTranslation()
  const { data: trips = [], isLoading } = useTravelingBooks(clubId)
  const { data: members = [] } = useClubMembers(clubId)
  const memberMap = useMemo(() => new Map(members.map((m) => [m.user_id, m])), [members])
  const [creating, setCreating] = useState(false)

  const traveling = trips.filter((trip) => trip.status === 'traveling')
  const home = trips.filter((trip) => trip.status === 'home')

  return (
    <Card className="border-0 shadow-sm min-w-0">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base font-semibold">🧳 {t('traveling.title')}</CardTitle>
          <Button size="sm" onClick={() => setCreating(true)}>
            {t('traveling.create')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="h-20 animate-pulse rounded bg-muted" />
        ) : trips.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('traveling.empty')}</p>
        ) : (
          <>
            {traveling.length > 0 && (
              <ul className="space-y-3">
                {traveling.map((trip) => (
                  <TripCard key={trip.id} trip={trip} members={memberMap} />
                ))}
              </ul>
            )}
            {home.length > 0 && (
              <details>
                <summary className="cursor-pointer text-sm text-muted-foreground">
                  {t('traveling.finished', { count: home.length })}
                </summary>
                <ul className="mt-3 space-y-3">
                  {home.map((trip) => (
                    <TripCard key={trip.id} trip={trip} members={memberMap} />
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </CardContent>

      {creating && <CreateTripModal clubId={clubId} onClose={() => setCreating(false)} />}
    </Card>
  )
}
